import { Money } from "components/shared/Money";
import graphql from "babel-plugin-relay/macro";
import classNames from "classnames";
import styles from "components/payment/PaymentRow.module.scss";
import { PaymentRowLicenceDetails } from "components/payment/PaymentRowLicenceDetails";
import { Message } from "shared/components/Message";
import { IPaymentState } from "domain/payment/combinedPaymentReducer";
import { paymentActions } from "domain/payment/paymentActions";
import { paymentMessages } from "domain/payment/paymentMessages";
import { redirectActions } from "domain/redirect/redirectActions";
import { PaymentRow_configuration } from "generatedQueries/PaymentRow_configuration.graphql";
import { PaymentRow_paymentRow } from "generatedQueries/PaymentRow_paymentRow.graphql";
import React, { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createFragmentContainer } from "react-relay";
import { Button, Tooltip } from "reactstrap";
import { routes } from "routes/routes";
import { RootState } from "store/store";
import { useIntl } from "react-intl";

interface IGraphQlProps {
    configuration: PaymentRow_configuration;
    paymentRow: PaymentRow_paymentRow;
    "data-testid"?: string;
}

const InternalPaymentRow: FC<IGraphQlProps> = ({
    configuration,
    paymentRow,
    "data-testid": dataTestId,
}) => {
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();
    const paymentState = useSelector<RootState, IPaymentState>(state => state.payment);
    const[displayRemoveTooltip, setDisplayRemoveTooltip] = useState(false);

    const { renewingAnimalsInCart } = paymentState;
    const { animalId, animalName, isLateFee, blocked } = paymentRow;

    const canBeRemoved = renewingAnimalsInCart.indexOf(animalId) >= 0;

    const removeButtonId = `remove-button-${animalId}`;
    const removeDescriptionId = `remove-desc-${animalId}`;

    return (
        <tr className={classNames(blocked && "table-warning")} data-testid={dataTestId}>
            <th scope="row" className="font-weight-normal">
                {animalName}
            </th>
            <td>
                <PaymentRowLicenceDetails
                    configuration={configuration}
                    paymentRow={paymentRow}
                />
            </td>
            <td>
                <Money amount={paymentRow.amount} currency={paymentRow.currency} />
            </td>
            <td className="text-nowrap">
                {!isLateFee && (
                    <div
                        role="group"
                        aria-label={formatMessage(paymentMessages.petActionsGroupLabel, { animalName })}
                    >
                        <Button
                            id={removeButtonId}
                            onClick={remove}
                            color="link"
                            className={classNames(styles.button, !canBeRemoved && "disabled text-muted")}
                            aria-label={formatMessage(paymentMessages.removeButtonSrLabel, { animalName })}
                            aria-disabled={!canBeRemoved}
                            aria-describedby={!canBeRemoved ? removeDescriptionId : undefined}
                            style={{ pointerEvents: "auto", textDecoration: "none" }}
                        >
                            <Message message={paymentMessages.removeButtonLabel} />
                        </Button>
                        {!canBeRemoved && (
                            <span id={removeDescriptionId} className="sr-only">
                                {formatMessage(paymentMessages.unableToRemoveFromPaymentTooltip)}
                            </span>
                        )}
                        <Button
                            onClick={edit}
                            color="link"
                            className={styles.button}
                            aria-label={formatMessage(paymentMessages.editButtonSrLabel, { animalName })}
                            style={{ textDecoration: "none" }}
                        >
                            <Message message={paymentMessages.editButtonLabel} />
                        </Button>
                        <Tooltip
                            placement="bottom"
                            isOpen={displayRemoveTooltip}
                            target={removeButtonId}
                            toggle={toggleRemoveTooltip}
                            aria-hidden="true"
                        >
                            <Message message={paymentMessages.unableToRemoveFromPaymentTooltip} />
                        </Tooltip>
                    </div>
                )}
            </td>
        </tr>
    );

    function toggleRemoveTooltip() {
        if (!canBeRemoved) {
            setDisplayRemoveTooltip(!displayRemoveTooltip);
        }
    }

    function edit() {
        dispatch(redirectActions.redirectTo(routes.pets.pet.create({ id: animalId })));
    }

    function remove() {
        if (canBeRemoved) {
            dispatch(paymentActions.removeRenewingAnimalFromCart(animalId));
        }
    }
};

export const PaymentRow = createFragmentContainer(InternalPaymentRow, {
    configuration: graphql`
        fragment PaymentRow_configuration on Configuration {
            organisationContact
            ...PaymentRowLicenceDetails_configuration
        }`,
    paymentRow: graphql`
        fragment PaymentRow_paymentRow on LicensingPaymentFormRow {
            amount
            currency
            animalId
            animalName
            blocked
            licenceType
            licenceTypeId
            rabiesIsValid
            isRenewal
            isLateFee
            ...PaymentRowLicenceDetails_paymentRow
        }`,
});
